// hooks/useDateRange.js
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { differenceInDays, subDays, startOfDay, endOfDay } from 'date-fns';

const DateRangeContext = createContext();

// Default date ranges for each page
const DEFAULT_DATE_RANGES = {
  dashboard: {
    from: startOfDay(subDays(new Date(), 6)),
    to: endOfDay(new Date()),
  },
  // Presence should behave like dashboard by default (last 7 days including today)
  presence: {
    from: startOfDay(subDays(new Date(), 6)),
    to: endOfDay(new Date()),
  },
  reviews: null, // Will be set dynamically based on earliest review date
};

export function DateRangeProvider({ children }) {
  // Page-specific date range states
  const [dashboardDateRange, setDashboardDateRange] = useState(DEFAULT_DATE_RANGES.dashboard);
  const [presenceDateRange, setPresenceDateRange] = useState(DEFAULT_DATE_RANGES.presence);
  const [reviewsDateRange, setReviewsDateRange] = useState(null); // null until initialized
  
  // Track which page is currently active
  const [activePage, setActivePage] = useState('dashboard');
  
  // Get the current date range based on active page
  let selectedDateRange;
  if (activePage === 'reviews' && reviewsDateRange) {
    selectedDateRange = reviewsDateRange;
  } else if (activePage === 'presence') {
    selectedDateRange = presenceDateRange;
  } else {
    // Fallback to dashboard as the default
    selectedDateRange = dashboardDateRange;
  }
  
  // Set date range for the current active page
  const setSelectedDateRange = useCallback((newRange) => {
    if (activePage === 'reviews') {
      setReviewsDateRange(newRange);
    } else if (activePage === 'presence') {
      setPresenceDateRange(newRange);
    } else {
      setDashboardDateRange(newRange);
    }
  }, [activePage]);

  // Initialize reviews date range (called from ReviewsLayout)
  const initializeReviewsDateRange = useCallback((earliestDate) => {
    if (!reviewsDateRange) {
      setReviewsDateRange({
        from: startOfDay(earliestDate),
        to: endOfDay(new Date()),
      });
    }
  }, [reviewsDateRange]);

  const [dateRangeDifference, setDateRangeDifference] = useState(7);

  useEffect(() => {
    const changedDateRangeDifference = differenceInDays(selectedDateRange.to, selectedDateRange.from) + 1;
    setDateRangeDifference(changedDateRangeDifference);
  }, [selectedDateRange]);

  return (
    <DateRangeContext.Provider value={{ 
      selectedDateRange, 
      setSelectedDateRange, 
      dateRangeDifference,
      activePage,
      setActivePage,
      initializeReviewsDateRange,
    }}>
      {children}
    </DateRangeContext.Provider>
  );
};

export function useDateRangeContext() {
  return useContext(DateRangeContext);
}